// src/pages/admin/DealsShelvesAdmin.jsx
import { useEffect, useState } from 'react'
import {
  fetchAllDealsShelves,
  createDealsShelf,
  updateDealsShelf,
  deleteDealsShelf,
} from '../../lib/dealsShelves'
import { fetchTopCategories } from '../../lib/categories'

const EMPTY_FORM = {
  title: '',
  categoryId: '',
  isActive: true,
  order: 0,
  limitProducts: 8,
  viewAllHref: '/catalog',
}

export default function DealsShelvesAdmin() {
  const [shelves, setShelves] = useState([])
  const [categories, setCategories] = useState([])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const loadShelves = async () => {
    try {
      setLoading(true)
      setError(null)
      const list = await fetchAllDealsShelves()
      setShelves(list)
    } catch (e) {
      console.error(e)
      setError(e?.message || 'Erreur de chargement des étagères.')
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      const cats = await fetchTopCategories()
      setCategories(cats)
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    loadShelves()
    loadCategories()
  }, [])

  const openNew = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setIsModalOpen(true)
  }

  const openEdit = (shelf) => {
    setEditing(shelf)
    setForm({
      ...EMPTY_FORM,
      ...shelf,
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'order' || name === 'limitProducts'
          ? Number(value)
          : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (!form.categoryId) {
        alert('Choisis une catégorie.')
        setSaving(false)
        return
      }

      if (editing) {
        await updateDealsShelf(editing.id, form)
      } else {
        await createDealsShelf(form)
      }
      closeModal()
      await loadShelves()
    } catch (e) {
      console.error(e)
      alert("Erreur lors de l'enregistrement.")
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = (shelf) => {
    setDeletingId(shelf.id)
    if (window.confirm('Supprimer cette étagère de deals ?')) {
      handleDelete(shelf.id)
    } else {
      setDeletingId(null)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteDealsShelf(id)
      await loadShelves()
    } catch (e) {
      console.error(e)
      alert('Erreur lors de la suppression.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="my-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Admin — Deals shelves</h3>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={openNew}
        >
          + Nouvelle étagère
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="alert alert-info">Chargement…</div>
      ) : shelves.length === 0 ? (
        <div className="alert alert-light border">
          Aucune étagère. Clique sur{' '}
          <strong>Nouvelle étagère</strong> pour en créer une.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Ordre</th>
                <th>Titre</th>
                <th>Catégorie</th>
                <th>Nb produits</th>
                <th>Actif</th>
                <th>Lien "View all"</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shelves.map((s, idx) => {
                const catLabel =
                  categories.find((c) => String(c.id) === String(s.categoryId))
                    ?.label || s.categoryId

                return (
                  <tr key={s.id}>
                    <td>{idx + 1}</td>
                    <td>{s.order ?? 0}</td>
                    <td>{s.title || '(sans titre)'}</td>
                    <td>{catLabel}</td>
                    <td>{s.limitProducts ?? 8}</td>
                    <td>
                      {s.isActive ? (
                        <span className="badge bg-success">Oui</span>
                      ) : (
                        <span className="badge bg-secondary">Non</span>
                      )}
                    </td>
                    <td className="small">{s.viewAllHref || '/catalog'}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => openEdit(s)}
                      >
                        Éditer
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => confirmDelete(s)}
                        disabled={deletingId === s.id}
                      >
                        {deletingId === s.id
                          ? 'Suppression…'
                          : 'Supprimer'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL contrôlé par React */}
      {isModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editing
                      ? 'Modifier une étagère de deals'
                      : 'Nouvelle étagère de deals'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    aria-label="Close"
                  />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Titre</label>
                      <input
                        type="text"
                        name="title"
                        className="form-control"
                        value={form.title}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Ordre</label>
                      <input
                        type="number"
                        name="order"
                        className="form-control"
                        value={form.order}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Catégorie source</label>
                      <select
                        name="categoryId"
                        className="form-select"
                        value={form.categoryId}
                        onChange={handleChange}
                      >
                        <option value="">-- Choisir une catégorie --</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <div className="form-text">
                        Les produits affichés seront pris dans cette
                        catégorie (champ <code>categoryId</code> des
                        produits).
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        Nombre max de produits
                      </label>
                      <input
                        type="number"
                        name="limitProducts"
                        className="form-control"
                        value={form.limitProducts}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-12">
                      <label className="form-label">Lien "View all"</label>
                      <input
                        type="text"
                        name="viewAllHref"
                        className="form-control"
                        value={form.viewAllHref}
                        onChange={handleChange}
                      />
                      <div className="form-text">
                        Exemple : <code>/catalog?cat=electronics</code>
                      </div>
                    </div>

                    <div className="col-12 d-flex align-items-center">
                      <div className="form-check mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="dsActiveCheck"
                          name="isActive"
                          checked={form.isActive}
                          onChange={handleChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="dsActiveCheck"
                        >
                          Étagère active (visible sur le site)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeModal}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving
                      ? 'Enregistrement…'
                      : editing
                      ? 'Enregistrer'
                      : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
